/**
 * Custom hook for managing content state and operations
 */

import { useState, useEffect, useMemo } from 'react'
import { api } from '../../../../services'

const isRecord = value =>
  value !== null && typeof value === 'object' && !Array.isArray(value)

const normalizeContent = value => {
  if (!isRecord(value)) {
    return {}
  }

  const normalized = {}

  Object.entries(value).forEach(([sectionTitle, subsections]) => {
    if (!isRecord(subsections)) {
      normalized[sectionTitle] = {}
      return
    }

    normalized[sectionTitle] = {}
    Object.entries(subsections).forEach(([subsectionTitle, subsectionValue]) => {
      if (isRecord(subsectionValue)) {
        normalized[sectionTitle][subsectionTitle] = subsectionValue
        return
      }

      if (typeof subsectionValue === 'string') {
        normalized[sectionTitle][subsectionTitle] = {
          generatedContent: subsectionValue
        }
        return
      }

      normalized[sectionTitle][subsectionTitle] = {}
    })
  })

  return normalized
}

export const useContentManagement = ({
  tableOfContent,
  onTableOfContentChange,
  language
}) => {
  const [content, setContent] = useState({})
  const [isAddingContentFromInternet, setIsAddingContentFromInternet] = useState(null)

  useEffect(() => {
    if (!tableOfContent || !tableOfContent.content) {
      setContent({})
      return
    }

    try {
      const parsedContent =
        typeof tableOfContent.content === 'string'
          ? JSON.parse(tableOfContent.content)
          : tableOfContent.content
      setContent(normalizeContent(parsedContent))
    } catch (error) {
      console.error('Failed to parse table of content:', error)
      setContent({})
    }
  }, [tableOfContent])

  const updateContent = newContent => {
    const normalizedContent = normalizeContent(newContent)
    setContent(normalizedContent)

    if (onTableOfContentChange && tableOfContent && tableOfContent.id) {
      onTableOfContentChange({
        tableOfContent: JSON.stringify(normalizedContent),
        id: tableOfContent.id
      })
    }
  }

  const handleDelete = (section, subsection) => {
    const newContent = { ...content }
    if (!subsection) {
      delete newContent[section]
    } else {
      if (!isRecord(newContent[section])) {
        return
      }
      delete newContent[section][subsection]
    }
    updateContent(newContent)
  }

  const handleAddTopic = (currentSection, newTitle) => {
    if (newTitle && currentSection !== null) {
      const newContent = { ...content }

      if (!newContent[currentSection]) {
        newContent[currentSection] = {}
      }

      const currentSectionObject = newContent[currentSection]
      currentSectionObject[`${newTitle}`] = {}
      updateContent(newContent)
    }
  }

  const handleAddLesson = (newLessonTitle) => {
    if (newLessonTitle) {
      const newContent = { ...content }
      newContent[`${newLessonTitle}`] = {}
      updateContent(newContent)
    }
  }

  const handleSaveEdit = (currentSection, currentSubsection, newTitle) => {
    if (newTitle && currentSection !== null) {
      const newContent = { ...content }

      if (currentSubsection === null) {
        // Editing lesson title without changing the order
        const lessonKeys = Object.keys(newContent)
        const lessonIndex = lessonKeys.indexOf(currentSection)
        const lessonTopics = newContent[currentSection]

        if (lessonIndex !== -1) {
          lessonKeys[lessonIndex] = newTitle
        }

        const updatedContent = {}
        lessonKeys.forEach(key => {
          updatedContent[key] = newContent[key]
        })

        updatedContent[newTitle] = lessonTopics
        updateContent(updatedContent)
      } else {
        // Editing topic title
        const topics = isRecord(newContent[currentSection])
          ? { ...newContent[currentSection] }
          : {}
        const updatedTopics = {}

        // Iterate through the topics, update the title if it matches currentSubsection
        Object.keys(topics).forEach(topic => {
          if (topic === currentSubsection) {
            updatedTopics[newTitle] = topics[topic]
          } else {
            updatedTopics[topic] = topics[topic]
          }
        })

        newContent[currentSection] = updatedTopics
        updateContent(newContent)
      }
    }
  }

  const handleAddContentFromInternet = (generatedContentModal, courseTitle, setGeneratedContentModal) => {
    const { content: generatedContent, title, lesson } = generatedContentModal

    setIsAddingContentFromInternet({
      topic: lesson,
      sub_topic: title
    })

    const payload = {
      course_title: courseTitle,
      topic: lesson,
      sub_topic: title,
      database_content: generatedContent,
      language
    }

    api.courses
      .generateContentFromInternet(payload)
      .then(res => {
        const text = res.data
        setGeneratedContentModal(pre => ({
          ...pre,
          content: text
        }))
        setContent(pre => ({
          ...pre,
          [lesson]: {
            ...pre[lesson],
            [title]: {
              generatedContent: text
            }
          }
        }))
      })
      .finally(() => {
        setIsAddingContentFromInternet(null)
      })
  }

  const progressStatus = useMemo(() => {
    if (!isRecord(content)) {
      return {
        totalSubtopics: 0,
        totalSubtopicsWithContent: 0,
        subTopicsWithError: 0
      }
    }

    // total subtopics inside the content
    const totalSubtopics = Object.keys(content).reduce((acc, section) => {
      const sectionContent = isRecord(content[section]) ? content[section] : {}
      return acc + Object.keys(sectionContent).length
    }, 0)

    // total subtopics inside the content with generated content
    const totalSubtopicsWithContent = Object.keys(content).reduce(
      (acc, section) => {
        const sectionContent = isRecord(content[section]) ? content[section] : {}
        return (
          acc +
          Object.keys(sectionContent).reduce((acc, subtopic) => {
            const subtopicContent = isRecord(sectionContent[subtopic])
              ? sectionContent[subtopic]
              : {}
            return acc + (subtopicContent.generatedContent ? 1 : 0)
          }, 0)
        )
      },
      0
    )

    const subTopicsWithError = totalSubtopics - totalSubtopicsWithContent

    return {
      totalSubtopics,
      totalSubtopicsWithContent,
      subTopicsWithError
    }
  }, [content])

  const isContentGeneratedSuccessfully = useMemo(() => {
    if (progressStatus.totalSubtopicsWithContent === 0) return false
    return (
      progressStatus.totalSubtopicsWithContent === progressStatus.totalSubtopics
    )
  }, [progressStatus])

  const contentSuccessInAllSections = useMemo(() => {
    if (!isRecord(content) || Object.keys(content).length === 0) {
      return false
    }

    const isSuccess = Object.keys(content).every(section =>
      Object.keys(isRecord(content[section]) ? content[section] : {}).every(subsection => {
        const contentItem =
          content && content[section] && content[section][subsection]
        const generatedContent = contentItem && contentItem.generatedContent
        const status = contentItem && contentItem.status

        return generatedContent && status !== 'error'
      })
    )

    return isSuccess
  }, [content])

  const checkError = () => {
    return Object.values(content).some(section => {
      const sectionValues = isRecord(section) ? Object.values(section) : []
      return sectionValues.some(subsection => {
        return isRecord(subsection) && subsection.status === 'error'
      })
    })
  }

  return {
    content,
    setContent,
    updateContent,
    isAddingContentFromInternet,
    setIsAddingContentFromInternet,
    handleDelete,
    handleAddTopic,
    handleAddLesson,
    handleSaveEdit,
    handleAddContentFromInternet,
    progressStatus,
    isContentGeneratedSuccessfully,
    contentSuccessInAllSections,
    checkError
  }
}
